import React, { useState, useEffect } from 'react';
import { Button } from 'src/components/ui/button';
import { useToast } from 'src/hooks/use-toast';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from 'src/components/ui/dialog';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format, parse } from 'date-fns';
import { Calendar } from 'src/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { Checkbox } from 'src/components/ui/checkbox';

const JoiningDetailsModal = ({
  isOpen,
  onClose,
  onSubmit,
  candidateId,
  opportunityId,
  initialJoiningDate,
  initialCtc,
}) => {
  const [joiningDate, setJoiningDate] = useState();
  const [ctc, setCtc] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verified, setVerified] = useState(false);
  const { toast } = useToast();

  // Update state when initial values are provided
  useEffect(() => {
    if (initialJoiningDate) {
      try {
        // Convert string date to Date object
        const parsedDate = parse(initialJoiningDate, 'dd/MM/yyyy', new Date());
        setJoiningDate(parsedDate);
      } catch (error) {
        console.error('Error parsing date:', error);
      }
    }

    if (initialCtc) {
      // Convert to number first to handle string/number, then to integer (remove decimals)
      const numericValue = Number(initialCtc);
      const formattedCtc = Math.floor(numericValue).toString();
      setCtc(formattedCtc);
    }
  }, [initialJoiningDate, initialCtc]);

  const handleCtcChange = (e) => {
    // Allow only numeric input
    const value = e.target.value.replace(/\D/g, '');
    setCtc(value);
  };

  const handleSubmit = () => {
    if (!joiningDate) {
      toast({
        message: 'Please select a joining date',
        variant: 'warning',
      });
      return;
    }

    if (!ctc) {
      toast({
        message: 'Please enter CTC',
        variant: 'warning',
      });
      return;
    }

    if (!verified) {
      toast({
        message: 'Please verify the details are correct',
        variant: 'warning',
      });
      return;
    }

    setIsSubmitting(true);

    onSubmit({
      candidate_id: candidateId,
      joining_date: format(joiningDate, 'dd/MM/yyyy'),
      ctc_amount: Number(ctc), // Convert to number
      requirement_id: Number(opportunityId), // Convert to number
    });
    setIsSubmitting(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Candidate Joining Details</DialogTitle>
          <DialogDescription>
            Set the confirmed joining date and CTC for this candidate.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="joining-date">Joining Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                  id="joining-date"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {joiningDate ? (
                    format(joiningDate, 'PPP')
                  ) : (
                    <span>Select date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={joiningDate}
                  onSelect={setJoiningDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ctc">CTC (Annual)</Label>
            <Input
              id="ctc"
              type="text"
              value={ctc}
              onChange={handleCtcChange}
              placeholder="Enter CTC amount (numbers only)"
            />
          </div>

          <div className="flex pt-2">
            <div className="flex-shrink-0 mt-0.5">
              <Checkbox
                id="verify"
                checked={verified}
                onCheckedChange={setVerified}
              />
            </div>
            <div className="ml-2">
              <Label
                htmlFor="verify"
                className="text-sm font-medium leading-normal cursor-pointer"
              >
                Verified and Confirmed
              </Label>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !verified}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Details'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default JoiningDetailsModal;
